// Pictures stay decoded across channel switches. Switching channels unmounts every row, and a browser is free to drop
// a picture nothing shows any more from its in-memory cache; coming back then means a trip to the disk cache and a
// fresh decode, so avatars, emoji and photos sat blank for a few hundred milliseconds. Holding an Image for each
// picture we've drawn keeps it in memory, and a picture we know is drawn at once (eager, synchronous decode)
// instead of waiting for lazy loading.

const CAP = 600;
const kept = new Map<string, HTMLImageElement>();

/** Keeps a picture that just finished loading (newest last; the oldest go past CAP). */
export function keepImage(src: string | undefined | null) {
  if (!src || typeof Image === "undefined") return;
  const had = kept.get(src);
  if (had) {
    kept.delete(src);
    kept.set(src, had);
    return;
  }
  const img = new Image();
  img.decoding = "async";
  img.src = src;
  kept.set(src, img);
  while (kept.size > CAP) kept.delete(kept.keys().next().value!);
}

/** Props for an <img> of `src`: drawn at once if we've shown it before, lazily otherwise; remembered when it loads. */
export function imgProps(src: string | undefined | null, lazy = true): { loading: "eager" | "lazy"; decoding: "sync" | "async"; onLoad: () => void } {
  const known = !!src && kept.has(src);
  return { loading: known || !lazy ? "eager" : "lazy", decoding: known ? "sync" : "async", onLoad: () => keepImage(src) };
}
